import type { SageOfficeView } from "../../sage-offices/types";
import { csvEscape } from "../../common/csv";
import { canonicalWorkspaceType, workspaceTypes, type BulkWorkspaceRow } from "../types";

/**
 * Per-row state in the parser output. Carries:
 *
 *   - `rowIndex`: 1-based source line number, matching the spreadsheet
 *     row the user sees ("row 1" is the header; data starts at row 2).
 *   - `payload`: the row in the same shape we'll POST. The id is
 *     generated *here* (parse time) and held for the lifetime of the
 *     page, so a retry of the same parsed table replays the same
 *     UUIDs and the backend's id-conflict path becomes "skipped".
 *   - `errors`: per-row validation messages. A non-empty list disables
 *     the import button until the user fixes the source CSV and
 *     re-uploads.
 */
export interface ParsedWorkspaceRow {
    readonly rowIndex: number;
    readonly payload: BulkWorkspaceRow;
    readonly errors: readonly string[];
    /** Resolved sage office name for the preview table. `null` when the id is unknown. */
    readonly sageOfficeName: string | null;
}

export interface ParseWorkspacesCsvResult {
    /** Top-level errors that prevented row-level parsing (no header, empty file, etc.). */
    readonly fatalError: string | null;
    readonly rows: readonly ParsedWorkspaceRow[];
    readonly hasErrors: boolean;
}

/**
 * Required columns in the expected order. We surface the user's
 * column-order mistakes as a fatal error (so they fix the file and
 * re-upload) rather than trying to be clever about flexible mapping —
 * the cost of "we silently swapped name and friendlyName" is worse
 * than asking the operator to download a fresh template.
 */
const REQUIRED_HEADERS = ["sageOfficeId", "type", "name"] as const;
const OPTIONAL_HEADERS = ["friendlyName", "floorplanElementId"] as const;
const ALL_HEADERS = [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS];

// Canonical type set, lowercased, so the CSV check accepts any case
// the operator typed. The stored payload still uses the canonical
// PascalCase form via `canonicalWorkspaceType` below — keeping the
// API surface consistent regardless of CSV casing quirks.
const TYPE_VALUES_LOWER = new Set<string>(workspaceTypes.map((t) => t.toLowerCase()));

/**
 * RFC 4180-lite CSV parser. Handles:
 *
 *   - CRLF and LF line endings
 *   - Quoted fields (`"..."`) including embedded commas and escaped quotes (`""`)
 *   - Trailing newline
 *
 * Doesn't handle: embedded newlines inside quoted fields. That's a
 * deliberate omission — the CSV format we're asking the user to
 * produce doesn't need it, and supporting it would double the parser
 * size for a feature nobody will use here.
 */
export function parseWorkspacesCsv(
    text: string,
    offices: readonly SageOfficeView[],
): ParseWorkspacesCsvResult {
    const officeIndex = new Map<string, SageOfficeView>();
    for (const o of offices) officeIndex.set(o.id, o);

    const lines = splitCsvLines(text);
    if (lines.length === 0) {
        return { fatalError: "The file appears to be empty.", rows: [], hasErrors: true };
    }

    const headers = parseCsvLine(lines[0] ?? "").map((h) => h.trim());
    const headerError = validateHeader(headers);
    if (headerError !== null) {
        return { fatalError: headerError, rows: [], hasErrors: true };
    }

    const colIndex = new Map<string, number>();
    headers.forEach((h, i) => colIndex.set(h, i));

    const rows: ParsedWorkspaceRow[] = [];
    let anyError = false;

    // Build a hint string with up to a few valid office name+id pairs
    // so the unknown-id error message has actionable recovery info
    // rather than just "no". Truncated to keep the error UI scannable.
    const activeOfficeHint = buildAvailableOfficesHint(offices);

    for (let i = 1; i < lines.length; i++) {
        const raw = lines[i];
        // Skip blank lines outright — common when someone exports from
        // Excel and trailing empties leak through.
        if (raw === undefined || raw.trim() === "") continue;

        const cells = parseCsvLine(raw);
        const sageOfficeId = (cells[colIndex.get("sageOfficeId") ?? -1] ?? "").trim();
        const type = (cells[colIndex.get("type") ?? -1] ?? "").trim();
        const name = (cells[colIndex.get("name") ?? -1] ?? "").trim();
        const friendlyRaw = (cells[colIndex.get("friendlyName") ?? -1] ?? "").trim();
        const friendlyName = friendlyRaw === "" ? null : friendlyRaw;
        const floorplanRaw = (cells[colIndex.get("floorplanElementId") ?? -1] ?? "").trim();
        const floorplanElementId = floorplanRaw === "" ? null : floorplanRaw;

        const errors: string[] = [];
        if (sageOfficeId === "") errors.push("sageOfficeId is required.");
        if (name === "") errors.push("name is required.");
        if (type === "") {
            errors.push("type is required.");
        } else if (!TYPE_VALUES_LOWER.has(type.toLowerCase())) {
            errors.push(`type must be one of: ${workspaceTypes.join(", ")}.`);
        }
        const officeMatch = sageOfficeId !== "" ? officeIndex.get(sageOfficeId) : undefined;
        if (sageOfficeId !== "" && officeMatch === undefined) {
            errors.push(
                activeOfficeHint !== null
                    ? `sageOfficeId '${sageOfficeId}' is not a known sage office. ${activeOfficeHint}`
                    : `sageOfficeId '${sageOfficeId}' is not a known sage office.`,
            );
        } else if (officeMatch !== undefined && officeMatch.isDeleted) {
            errors.push(`sageOfficeId '${sageOfficeId}' refers to a deleted sage office.`);
        }
        if (friendlyName !== null && friendlyName.length > 100) {
            errors.push("friendlyName must be 100 characters or fewer.");
        }
        if (floorplanElementId !== null && floorplanElementId.length > 64) {
            errors.push("floorplanElementId must be 64 characters or fewer.");
        }

        if (errors.length > 0) anyError = true;

        rows.push({
            rowIndex: i + 1,
            payload: {
                id: crypto.randomUUID(),
                sageOfficeId,
                // Canonicalize so the import payload matches what the
                // form/Select would produce — keeps the stored value
                // identical regardless of whether the operator typed
                // "Room", "room", or "ROOM" in the CSV.
                type: type === "" ? type : canonicalWorkspaceType(type),
                name,
                friendlyName,
                floorplanElementId,
            },
            errors,
            sageOfficeName: officeMatch?.name ?? null,
        });
    }

    if (rows.length === 0) {
        return {
            fatalError: "No data rows found below the header.",
            rows: [],
            hasErrors: true,
        };
    }

    return { fatalError: null, rows, hasErrors: anyError };
}

function validateHeader(headers: readonly string[]): string | null {
    const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
    if (missing.length > 0) {
        return `Header is missing required column(s): ${missing.join(", ")}. Expected: ${ALL_HEADERS.join(", ")}.`;
    }
    const unknown = headers.filter(
        (h) => h !== "" && !ALL_HEADERS.includes(h as (typeof ALL_HEADERS)[number]),
    );
    if (unknown.length > 0) {
        return `Header has unrecognized column(s): ${unknown.join(", ")}. Expected: ${ALL_HEADERS.join(", ")}.`;
    }
    return null;
}

/**
 * Split the file into raw lines on CRLF/LF, dropping the final blank
 * if the file ends with a newline.
 */
function splitCsvLines(text: string): string[] {
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalized.split("\n");
    if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
    return lines;
}

/**
 * Parse a single CSV line into cells. Quote-aware so cells like
 * `"Smith, John"` or `"She said ""hi"""` survive intact.
 */
function parseCsvLine(line: string): string[] {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    // Escaped quote inside a quoted field.
                    current += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
                i++;
                continue;
            }
            current += ch;
            i++;
            continue;
        }
        if (ch === ",") {
            cells.push(current);
            current = "";
            i++;
            continue;
        }
        if (ch === '"' && current === "") {
            inQuotes = true;
            i++;
            continue;
        }
        current += ch;
        i++;
    }
    cells.push(current);
    return cells;
}

/**
 * Build the CSV template the user can download to seed their file.
 * Includes one example row of each workspace type so the operator
 * has a working pattern to copy from.
 *
 * When at least one active office is loaded, the example rows use a
 * real office id from the current tenant — that way the template is
 * immediately importable as-is and the operator never has to guess
 * a UUID format. Falls back to a clearly-fake placeholder when no
 * active offices are available, so the template still parses but
 * surfaces obvious "fix me" markers in the preview.
 */
export function buildWorkspaceCsvTemplate(offices: readonly SageOfficeView[] = []): string {
    const exampleOffice = offices.find((o) => !o.isDeleted) ?? null;
    const exampleId = exampleOffice?.id ?? "<paste-office-id-here>";
    const header = ALL_HEADERS.join(",");
    const examples = [
        `${csvEscape(exampleId)},Room,Room 332,I.T. Collaboration Room`,
        `${csvEscape(exampleId)},Desk,Desk 14B,`,
        `${csvEscape(exampleId)},Office,Corner Office,Old Boardroom`,
    ];
    return [header, ...examples].join("\n") + "\n";
}

/**
 * Render up to a handful of office name+id pairs to embed inside an
 * unknown-id error message. Bounded so a tenant with hundreds of
 * offices doesn't produce a wall-of-text per failed row.
 */
function buildAvailableOfficesHint(offices: readonly SageOfficeView[]): string | null {
    const active = offices.filter((o) => !o.isDeleted);
    if (active.length === 0) return null;
    const SAMPLE = 3;
    const sample = active
        .slice(0, SAMPLE)
        .map((o) => `'${o.name}' (${o.id})`)
        .join(", ");
    if (active.length <= SAMPLE) {
        return `Available: ${sample}.`;
    }
    return `Available (${SAMPLE} of ${active.length}): ${sample}.`;
}

/**
 * Build a CSV from the failed rows of a previous import so the
 * operator can fix and re-run only the rejects. Includes the original
 * column shape plus the server's per-row error message in a trailing
 * `error` column the parser will reject as unknown — that's
 * intentional, the user is expected to delete the column before
 * re-uploading.
 */
export function buildFailedRowsCsv(
    failed: readonly { row: ParsedWorkspaceRow; errorMessage: string }[],
): string {
    const header = [...ALL_HEADERS, "error"].join(",");
    const lines = failed.map(({ row, errorMessage }) =>
        [
            csvEscape(row.payload.sageOfficeId),
            csvEscape(row.payload.type),
            csvEscape(row.payload.name),
            csvEscape(row.payload.friendlyName ?? ""),
            csvEscape(row.payload.floorplanElementId ?? ""),
            csvEscape(errorMessage),
        ].join(","),
    );
    return [header, ...lines].join("\n") + "\n";
}
